import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Area, AreaChart, Bar, BarChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Headphones, Users, Disc3, Repeat, Activity } from "lucide-react";
import { api } from "@/lib/api";
import { fmtNum } from "@/lib/hubs";
import { useAuth } from "@/context/AuthContext";
import { UpgradeCTA } from "@/components/UpgradeCTA";
import { Progress } from "@/components/ui/progress";

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="surface p-5">
    <Icon size={18} className="text-[var(--hub)]" />
    <div data-testid={testId} className="font-display text-4xl font-black mt-4">{value}</div>
    <div className="eyebrow mt-1">{label}</div>
  </div>
);

export const Header = ({ eyebrow, title, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 reveal">
    <div><div className="eyebrow">{eyebrow}</div><h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mt-1">{title}</h1></div>
    {children}
  </div>
);

export const Overview = () => {
  const { user } = useAuth();
  const [o, setO] = useState(null);
  useEffect(() => { api.get("/me/overview").then((r) => setO(r.data)); }, []);
  if (!o) return null;
  return (
    <div data-testid="dashboard-overview">
      <Header eyebrow={`${user.plan} plan`} title={`Hey ${user.name.split(" ")[0]} —`}>
        <Link to={`/artist/${user.slug}`} data-testid="view-public-profile" className="h-10 px-5 inline-flex items-center rounded-full border border-white/15 text-sm hover:border-white/40">View public profile</Link>
      </Header>
      <div className="grid sm:grid-cols-3 gap-4">
        <Stat icon={Headphones} label="Total streams" value={fmtNum(o.total_streams)} testId="overview-total-streams" />
        <Stat icon={Users} label="Followers" value={fmtNum(o.followers)} testId="overview-followers" />
        <Stat icon={Disc3} label="Public tracks" value={o.tracks} testId="overview-tracks" />
      </div>
      <div className="surface p-6 mt-4">
        <div className="flex justify-between text-sm"><span>Active tracks</span><span className="font-mono" data-testid="overview-quota">{o.active_tracks} / {o.track_cap ?? "∞"}</span></div>
        {o.track_cap && <Progress value={(o.active_tracks / o.track_cap) * 100} className="mt-3 h-2 bg-white/10" />}
        <div className="flex justify-between text-sm mt-4 text-zinc-400"><span>SmartMastering credits</span><span className="font-mono" data-testid="overview-credits">{o.mastering_credits}</span></div>
        <div className="flex justify-between text-sm mt-2 text-zinc-400"><span>Daily upload cap</span><span className="font-mono">{o.daily_cap} / 24h</span></div>
      </div>
      {o.plan === "free" && <div className="mt-6"><UpgradeCTA /></div>}
    </div>
  );
};

const COLORS = ["#FF3366", "#8B5CF6", "#00F2FE", "#F59E0B", "#10B981", "#3B82F6"];
const Card = ({ title, children, testId }) => <div data-testid={testId} className="surface p-5"><div className="eyebrow mb-4">{title}</div>{children}</div>;
const tip = { contentStyle: { background: "#12121A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12 } };

export const Analytics = () => {
  const [d, setD] = useState(null);
  const [locked, setLocked] = useState(false);
  useEffect(() => {
    api.get("/v1/artists/me/analytics").then((r) => setD(r.data)).catch((e) => e.response?.status === 402 && setLocked(true));
  }, []);
  if (locked) return (
    <div data-testid="analytics-locked">
      <Header eyebrow="Business intelligence" title="Analytics" />
      <div className="relative">
        <div className="grid sm:grid-cols-3 gap-4 blur-md pointer-events-none select-none opacity-50" aria-hidden>
          {[1, 2, 3].map((i) => <div key={i} className="surface h-40" />)}
          <div className="surface h-64 sm:col-span-3" />
        </div>
        <div className="absolute inset-0 flex items-center justify-center p-4"><div className="max-w-xl w-full"><UpgradeCTA testId="analytics-upgrade-cta" /></div></div>
      </div>
    </div>
  );
  if (!d) return <div className="h-64 rounded-3xl bg-white/5 animate-pulse" />;
  const s = d.summary;
  return (
    <div data-testid="analytics-dashboard">
      <Header eyebrow="Last 30 days" title="Analytics" />
      <div className="grid sm:grid-cols-4 gap-4">
        <Stat icon={Activity} label="Streams · 30d" value={fmtNum(s.streams_30d)} testId="analytics-streams-30d" />
        <Stat icon={Users} label="Unique listeners" value={fmtNum(s.unique_listeners)} testId="analytics-unique" />
        <Stat icon={Repeat} label="Retention rate" value={`${s.retention_rate}%`} testId="analytics-retention" />
        <Stat icon={Headphones} label="All-time streams" value={fmtNum(s.total_streams)} testId="analytics-total" />
      </div>
      <div className="mt-4"><Card title="Stream progression" testId="analytics-progression">
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={d.progression}><defs><linearGradient id="g" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor="#FF3366" stopOpacity={0.5} /><stop offset="100%" stopColor="#FF3366" stopOpacity={0} /></linearGradient></defs>
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#71717a" }} tickFormatter={(v) => v.slice(5)} /><YAxis tick={{ fontSize: 10, fill: "#71717a" }} width={40} /><Tooltip {...tip} />
            <Area type="monotone" dataKey="streams" stroke="#FF3366" strokeWidth={2} fill="url(#g)" /></AreaChart>
        </ResponsiveContainer></Card></div>
      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        <Card title="Countries" testId="analytics-countries">
          <ResponsiveContainer width="100%" height={260}><BarChart data={d.countries} layout="vertical"><XAxis type="number" hide /><YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: "#a1a1aa" }} /><Tooltip {...tip} /><Bar dataKey="streams" fill="#8B5CF6" radius={[0, 6, 6, 0]} /></BarChart></ResponsiveContainer>
        </Card>
        <Card title="Top cities" testId="analytics-cities">
          <div className="space-y-2">{d.cities.map((c) => <div key={c.city} className="flex justify-between text-sm py-1.5 border-b border-white/5"><span>{c.city} <span className="text-zinc-500 text-xs">{c.country}</span></span><span className="font-mono">{fmtNum(c.streams)}</span></div>)}</div>
        </Card>
        <Card title="Age" testId="analytics-age">
          <ResponsiveContainer width="100%" height={200}><BarChart data={d.demographics.age}><XAxis dataKey="range" tick={{ fontSize: 11, fill: "#a1a1aa" }} /><YAxis hide /><Tooltip {...tip} /><Bar dataKey="pct" fill="#00F2FE" radius={[6, 6, 0, 0]} /></BarChart></ResponsiveContainer>
        </Card>
        <Card title="Gender" testId="analytics-gender">
          <ResponsiveContainer width="100%" height={200}><PieChart><Pie data={d.demographics.gender} dataKey="pct" nameKey="label" innerRadius={50} outerRadius={80} paddingAngle={3}>{d.demographics.gender.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}</Pie><Tooltip {...tip} /></PieChart></ResponsiveContainer>
        </Card>
        <Card title="Traffic sources" testId="analytics-sources">
          <div className="space-y-2">{d.traffic_sources.map((t, i) => <div key={t.source} className="flex items-center gap-3 text-sm"><span className="w-2 h-2 rounded-full" style={{ background: COLORS[i % 6] }} /><span className="flex-1 capitalize">{t.source.replace("_", " ")}</span><span className="font-mono">{fmtNum(t.streams)}</span></div>)}</div>
        </Card>
        <Card title="Top tracks" testId="analytics-top-tracks">
          <div className="space-y-2">{d.top_tracks.map((t) => <div key={t.track_id} className="flex justify-between text-sm py-1"><span className="truncate">{t.title}</span><span className="font-mono">{fmtNum(t.streams)}</span></div>)}</div>
        </Card>
      </div>
      <p className="text-xs font-mono text-zinc-600 mt-4">Demographics, cities and retention are modeled estimates from aggregated listening data.</p>
    </div>
  );
};
